import { Controller } from "@hotwired/stimulus"

// The credit management radios' generated ids, `:last-child:checked`, and the document-wide
// `.${id}` payment panel lookup in showPaymentOption are load-bearing DOM contracts the spec
// pins, so that one seam is kept on jQuery exactly as it was. Everything else is native.
export default class extends Controller {
  static get targets() {
    return [
      "teamNameInput",
      "teamLogo",
      "logoColorInput",
      "colorSelectorButton",
      "colorPanel",
      "colorButton",
      "paymentOptions",
      "transferCreditsInput",
      "transferPlatformCreditsInput",
      "availableBalance",
      "availablePlatformBalance"
    ];
  }

  static get classes() {
    return ["invalid", "danger", "selected"];
  }

  connect() {
    // Pinned jQuery seam -- see file header.
    this.$radios = $(this.element).find(".organization_team_credit_management")
    this.$submitInput = $(this.element).find("input[type='submit']")
    this.$paymentOptions = $(this.paymentOptionsTarget)

    this.enableSaveButton()
    // Open the payment options box for the selected radio when the page loads
    this.showPaymentOption(this.$radios.find(".radio input:checked").attr("id"))
  }

  // Classes API values are only declared where a view opts into overriding them -- fall back to
  // the literal every existing view already renders rather than risk a bare `this.xClass` throw.
  get invalidClassName() {
    return this.hasInvalidClass ? this.invalidClass : "is-invalid"
  }

  get dangerClassName() {
    return this.hasDangerClass ? this.dangerClass : "text-danger"
  }

  get selectedClassName() {
    return this.hasSelectedClass ? this.selectedClass : "selected"
  }

  enableSaveButton(disable = false) {
    const creditsInputVal = parseFloat(this.transferCreditsInputTarget.value) || 0
    const platformCreditsInputVal = this.hasTransferPlatformCreditsInputTarget ?
      parseFloat(this.transferPlatformCreditsInputTarget.value) || 0 : 0

    if (this.teamNameInputTarget.value.length === 0) {
      disable = true
    }

    if (this.$radios.find("input[type='radio']:last-child:checked").val() === "restricted") {
      const availableCredits = Number(this.availableBalanceTarget.dataset.credits)
      const availablePlatformCredits = this.hasAvailablePlatformBalanceTarget ?
        Number(this.availablePlatformBalanceTarget.dataset.credits) : 0

      if (creditsInputVal > availableCredits || platformCreditsInputVal > availablePlatformCredits) {
        disable = true
      }
      // Should still allow "transfering" 0
      if (creditsInputVal < 0 || platformCreditsInputVal < 0) {
        disable = true
      }
    }

    if (!this.$radios.find("input[type='radio']:checked").val()) {
      disable = true
    }

    this.$submitInput.prop("disabled", disable)
  }

  // Pinned jQuery seam -- see file header.
  showPaymentOption(id) {
    this.$paymentOptions.find(".option").hide()
    $(`.${id}`).show()
  }

  transferCredits() {
    const overflowError = this.paymentOptionsTarget.querySelector(".transfer-credits .error.too-many")
    const negativeError = this.paymentOptionsTarget.querySelector(".transfer-credits .error.too-few")
    const availableCredits = Number(this.availableBalanceTarget.dataset.credits)
    const inputValue = parseFloat(this.transferCreditsInputTarget.value || 0)
    const newValue = availableCredits - inputValue

    if (inputValue >= 0) {
      this.availableBalanceTarget.textContent = newValue.toLocaleString("en-US")
    }

    if (newValue < 0) {
      this.availableBalanceTarget.textContent = "0"
    }

    if (inputValue > availableCredits) {
      this.transferCreditsInputTarget.classList.add(this.invalidClassName)
      this.availableBalanceTarget.classList.add(this.dangerClassName)
      overflowError.style.display = "block"
    } else if (inputValue < 0) { // Should still allow "transfering" 0
      this.transferCreditsInputTarget.classList.add(this.invalidClassName)
      this.availableBalanceTarget.classList.add(this.dangerClassName)
      negativeError.style.display = "block"
    } else {
      this.transferCreditsInputTarget.classList.remove(this.invalidClassName)
      this.availableBalanceTarget.classList.remove(this.dangerClassName)
      overflowError.style.display = "none"
      negativeError.style.display = "none"
    }

    this.enableSaveButton()
  }

  transferPlatformCredits() {
    if (!this.hasTransferPlatformCreditsInputTarget) {
      return
    }

    const overflowError = this.paymentOptionsTarget.querySelector(".transfer-platform-credits .error.too-many")
    const negativeError = this.paymentOptionsTarget.querySelector(".transfer-platform-credits .error.too-few")
    const availablePlatformCredits = Number(this.availablePlatformBalanceTarget.dataset.credits)
    const inputValue = parseFloat(this.transferPlatformCreditsInputTarget.value || 0)
    const newValue = availablePlatformCredits - inputValue

    if (inputValue >= 0 && newValue >= 0) {
      this.availablePlatformBalanceTarget.textContent = newValue.toLocaleString("en-US")
    }

    if (newValue < 0) {
      this.availablePlatformBalanceTarget.textContent = "0"
    }

    if (inputValue > availablePlatformCredits) {
      this.transferPlatformCreditsInputTarget.classList.add(this.invalidClassName)
      this.availablePlatformBalanceTarget.classList.add(this.dangerClassName)
      overflowError.style.display = "block"
    } else if (inputValue < 0) { // Should still allow "transfering" 0
      this.transferPlatformCreditsInputTarget.classList.add(this.invalidClassName)
      this.availablePlatformBalanceTarget.classList.add(this.dangerClassName)
      negativeError.style.display = "block"
    } else {
      this.transferPlatformCreditsInputTarget.classList.remove(this.invalidClassName)
      this.availablePlatformBalanceTarget.classList.remove(this.dangerClassName)
      overflowError.style.display = "none"
      negativeError.style.display = "none"
    }

    this.enableSaveButton()
  }

  onTeamNameKeyup(event) {
    // Get the initials
    const text = event.target.value
    const words = text.split(" ").filter((str) => str)
    if (text.length === 0) {
      this.teamLogoTarget.textContent = ""
    } else {
      this.teamLogoTarget.textContent = words.length > 1 ? words[0][0] + words[1][0] : words[0][0]
    }

    this.enableSaveButton()
  }

  onColorSelectorClick(event) {
    event.preventDefault()

    const isVisible = getComputedStyle(this.colorPanelTarget).display !== "none"
    this.colorPanelTarget.style.display = isVisible ? "none" : "block"
  }

  onColorButtonClick(event) {
    const selectedColor = event.target
    // Reset team logo color before overwriting
    this.teamLogoTarget.className = `team-logo lg ${selectedColor.className}`.trim()
    this.colorButtonTargets.forEach((button) => {
      button.innerHTML = ""
      button.classList.remove(this.selectedClassName)
    })
    this.logoColorInputTarget.value = selectedColor.className
    // Static literal, identical to what the server renders for the initially-selected color.
    selectedColor.innerHTML = "<i class='icon-lasso-check'></i>"
    selectedColor.classList.add(this.selectedClassName)
  }

  onCreditManagementChange(event) {
    this.showPaymentOption(event.target.id)
    this.enableSaveButton()
  }
}
